import { TicketStatus, TicketSummaryDto, isOwnershipClaimType } from '@api/types/tickets'

/**
 * Which moderator actions the ticket page offers, from the ticket and the viewer.
 *
 * The server lets staff resolve, close and reject from any status that is not Closed (Resolve and
 * Close never needed Start Progress first). The one rule kept on top of that is a courtesy: a
 * ticket someone else has claimed is theirs to act on, unless the viewer is an Admin.
 */
export interface TicketActionInput {
  status: TicketStatus
  claimedByUserId?: string | null
  isEscalated?: boolean
  /** The ticket's type: an ownership claim is approved/rejected, never resolved. */
  type?: number
  /** Ownership claims: the API says this viewer may decide it. */
  canDecideClaim?: boolean
}

export interface TicketActionFlags {
  claimedByMe: boolean
  claimedByOther: boolean
  canClaim: boolean
  canUnclaim: boolean
  canStartProgress: boolean
  canMarkAwaiting: boolean
  canResolve: boolean
  canReject: boolean
  canClose: boolean
  canReopen: boolean
  canEscalate: boolean
  /** Ownership claims: Approve / Reject (the claim's own endpoints, not Resolve / Reject). */
  canDecideClaim: boolean
}

/** Ids compared case-, whitespace- and hyphen-insensitively (GUIDs arrive in more than one shape). */
export const normalizeId = (id: string | null | undefined): string =>
  id ? id.toLowerCase().trim().replace(/[-\s]/g, '') : ''

export function getTicketActions(
  ticket: TicketActionInput,
  userId: string | null | undefined,
  roles: readonly string[] | null | undefined
): TicketActionFlags {
  const me = normalizeId(userId)
  const claimer = normalizeId(ticket.claimedByUserId)
  const claimedByMe = !!me && claimer === me
  const claimedByOther = !!claimer && !claimedByMe
  const isAdmin = roles?.includes('Admin') ?? false
  const mayAct = !claimedByOther || isAdmin

  const closed = ticket.status === TicketStatus.Closed
  const open = !closed && ticket.status !== TicketStatus.Resolved
  const claim = ticket.type !== undefined && isOwnershipClaimType(ticket.type)

  return {
    claimedByMe,
    claimedByOther,
    canClaim: ticket.status === TicketStatus.Pending && !claimer,
    canUnclaim: claimedByMe && ticket.status === TicketStatus.Claimed,
    canStartProgress: claimedByMe && ticket.status === TicketStatus.Claimed,
    canMarkAwaiting: claimedByMe && ticket.status === TicketStatus.InProgress,
    // A claim is settled by Approve/Reject below: the API refuses Resolve on it, and the generic
    // reject would close it without telling the owner side.
    canResolve: mayAct && open && !claim,
    canReject: mayAct && open && !claim,
    // An open claim is closed by Reject claim, which tells both sides.
    canClose: mayAct && !closed && !(claim && open),
    canReopen: ticket.status === TicketStatus.Resolved || closed,
    canEscalate:
      claimedByMe &&
      (ticket.status === TicketStatus.Claimed ||
        ticket.status === TicketStatus.InProgress ||
        ticket.status === TicketStatus.AwaitingUser) &&
      !ticket.isEscalated &&
      !claim,
    canDecideClaim: claim && open && !!ticket.canDecideClaim,
  }
}

/** Card-view order; the table sorts by its own column headers. */
export type CardSort = 'server' | 'votes' | 'escalation'

/** Up minus down, whichever form the DTO carries. */
export function ticketScore(t: { voteScore?: number; upVotes?: number; downVotes?: number }): number {
  return typeof t.voteScore === 'number' ? t.voteScore : (t.upVotes ?? 0) - (t.downVotes ?? 0)
}

/**
 * Client-side order for the card view (the pool lists come back priority-first from the API and
 * have no vote/escalation sort of their own): most votes first, or most escalated then oldest.
 */
export function sortTicketsForCards(tickets: TicketSummaryDto[], sort: CardSort): TicketSummaryDto[] {
  if (sort === 'server') return tickets
  const copy = [...tickets]
  if (sort === 'votes') copy.sort((a, b) => ticketScore(b) - ticketScore(a))
  else
    copy.sort(
      (a, b) =>
        (b.escalationLevel ?? 0) - (a.escalationLevel ?? 0) ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
  return copy
}

/** The reject reason the server accepts (CloseTicketDto.MaxReasonLength). */
export const MAX_CLOSE_REASON_LENGTH = 1000

/** The hover text on the "Paused" marker beside an escalation tag. */
export const ESCALATION_PAUSED_HINT = 'Waiting on the submitter; the escalation clock is paused'

/**
 * Whether the escalation tag carries a "Paused" marker: the API says the clock is stopped while the
 * ticket waits on the submitter. Falls back to nothing when an older row has no flag.
 */
export function isEscalationPaused(ticket: { escalationPaused?: boolean | null }): boolean {
  return ticket.escalationPaused === true
}

/**
 * Ownership claims: what the "Tell the current owner who claimed it" box in the approve and reject
 * dialogs starts at — the claim's current setting (off unless a moderator turned it on).
 */
export function claimRevealDefault(ticket: { claimantRevealedToOwner?: boolean | null }): boolean {
  return ticket.claimantRevealedToOwner === true
}

/**
 * The revealClaimant value a decision sends: only when the moderator changed the box, so a decision
 * never silently rewrites a setting someone else changed meanwhile.
 */
export function revealClaimantForDecision(current: boolean, chosen: boolean): boolean | undefined {
  return chosen === current ? undefined : chosen
}
